import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import validatePassword from "@insite/client-framework/Store/CommonHandlers/ValidatePassword";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import signIn from "@insite/client-framework/Store/Context/Handlers/SignIn";
import { getCurrentCountries } from "@insite/client-framework/Store/Data/Countries/CountriesSelectors";
import loadCurrentCountries from "@insite/client-framework/Store/Data/Countries/Handlers/LoadCurrentCountries";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import { getCreateAccountReturnUrl } from "@insite/client-framework/Store/Pages/CreateAccount/CreateAccountSelectors";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { RadioGroupComponentProps } from "@insite/mobius/RadioGroup";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";
import FieldSetPresentationProps, { FieldSetGroupPresentationProps } from "@insite/mobius/utilities/fieldSetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CreateAccountNewForm from "@tCooneyBrothers/Components/CreateAccountNewForm";
import Radio, { RadioComponentProps } from "@tCooneyBrothers/Components/CustomRadio";
import addAccount from "@tCooneyBrothers/Handlers/AddAccount";
import loadDepartments from "@tCooneyBrothers/Handlers/LoadDepartments";
import CustomApplicationState from "@tCooneyBrothers/Store/CustomApplicationState";
import React, { FC } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps extends WidgetProps {}

const mapDispatchToProps = {
    addAccount,
    signIn,
    validatePassword,
    loadDepartments,
    loadCurrentCountries,
};

const mapStateToProps = (state: CustomApplicationState) => ({
    accountSettings: getSettingsCollection(state).accountSettings,
    returnUrl: getCreateAccountReturnUrl(state),
    // To do: Add selector for fetching department values from Store
    departments: state.cbData?.departments?.value,
    countries: getCurrentCountries(state),
    shouldLoadCountries: !getCurrentCountries(state),
    restrictedAccounts: state.context?.settings?.properties?.restrictedAccountsForRegistration,
    homePageUrl: getPageLinkByPageType(state, "HomePage")?.url,
});

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

interface MessageFilterStyles {
    radioGroup?: FieldSetGroupPresentationProps<RadioGroupComponentProps>;
    radioButton?: FieldSetPresentationProps<RadioComponentProps>;
}
export interface CreateAccountStyles {
    spinner?: LoadingSpinnerProps;
    centeringWrapper?: InjectableCss;
    formsTopNav?: InjectableCss;
    messageFilter?: MessageFilterStyles;
    displayInlineBlock?: InjectableCss;
    paddingRight?: InjectableCss;
    formHeadingWrapper?: InjectableCss;
    formSelectionWrapper?: InjectableCss;
    CreateExistingCustomerWrapper?: InjectableCss;
    ExistingCustomerWrapper?: InjectableCss;
    CreateNewCustomerWrapper?: InjectableCss;
}

const styles: CreateAccountStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            height: 300px;
            justify-content: center;
            align-items: center;
        `,
    },
    formsTopNav: {
        css: css`
            display: flex;
            color: blue;
        `,
    },
    messageFilter: {
        radioGroup: {
            css: css`
                margin: 10px 0;
            `,
        },
        radioButton: {
            css: css`
                margin-bottom: 10px;
                &:last-of-type {
                    margin-bottom: 0;
                }
            `,
        },
    },
    // displayInlineBlock: {
    //     css: css`
    //         display: inline-block;
    //         vertical-align: top;
    //     `,
    // },
    paddingRight: {
        css: css`
            padding-right: 15px;
            font-weight: bold;
        `,
    },
    CreateExistingCustomerWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
    CreateNewCustomerWrapper: {
        css: css`
            font-weight: bold;
        `,
    },
    ExistingCustomerWrapper: {
        css: css`
            font-style: italic;
        `,
    },
    formHeadingWrapper: {
        css: css`
            display: inline-block;
            > div[class^="RadioStyle"] {
                margin-top: 0;
                margin-left: 10px;
            }
        `,
    },
    formSelectionWrapper: {
        css: css`
            // display: flex;
            // padding-bottom: 30px;
            // border-bottom: 1px solid #ef3742;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(
                    theme,
                    [
                        null,
                        null,
                        css`
                            flex-direction: column;
                        `,
                    ],
                    "max",
                )}
            > div > div[class^="RadioGroupStyle"] {
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(
                        theme,
                        [
                            null,
                            null,
                            css`
                                justify-content: flex-start;
                                margin-top: 10px;
                            `,
                        ],
                        "max",
                    )}
                > div[class^="RadioStyle"] {
                    margin-top: 0;
                    margin-right: 10px;
                    > label[ class^="TypographyStyle"]{
                        font-size:13px;
                    },
                }
            }
        `,
    },
};

export const createAccountStyles = styles;

const CreateNewCustomer: FC<Props> = ({
    addAccount,
    signIn,
    validatePassword,
    accountSettings,
    returnUrl,
    loadDepartments,
    departments,
    countries,
    shouldLoadCountries,
    loadCurrentCountries,
    homePageUrl,
}) => {
    React.useEffect(() => {
        if (!departments || departments.length <= 1) {
            loadDepartments();
        }
        if (shouldLoadCountries) {
            loadCurrentCountries();
        }
    }, []);

    return (
        <StyledWrapper>
            {countries && departments ? (
                <CreateAccountNewForm
                    addAccount={addAccount}
                    accountSettings={accountSettings}
                    returnUrl={returnUrl}
                    signIn={signIn}
                    validatePassword={validatePassword}
                    departments={departments}
                    countries={countries}
                    isCreateNewCustomerPage={true}
                    homePageUrl={homePageUrl}
                />
            ) : (
                <LoadingSpinner {...styles.spinner} />
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateNewCustomer),
    definition: {
        group: "Create Account",
        isSystem: true,
    },
};

export default widgetModule;
